"use client";

import React, { useState, useEffect } from "react";
import { cn, Chip } from "@heroui/react";

import { useEstimatedTime } from "../hooks/useEstimatedTime";

import Options from "./Options";
import RecommendationsDisplay from "./RecommendationsDisplay";
import WizardNavigation from "./WizardNavigation";
import { useAnswerContext } from "./AnswerContext";

export default function OnboardingWizard() {
  const {
    journeyData,
    answers,
    currentQuestionConfig,
    showRecommendations,
    isWelcomeScreenDismissed,
    handleSelectAnswer,
    isQuestionRevisited,
    currentQuestionId,
  } = useAnswerContext();
  const { remainingSeconds } = useEstimatedTime();

  // Format remaining time for display in chip
  const formatTimeForChip = (seconds: number): string => {
    if (seconds === 0) return "Done!";
    if (seconds <= 10) return "Almost done!";
    if (seconds <= 60) return `${seconds}s left`;
    const minutes = Math.ceil(seconds / 60);

    return `${minutes}min left before finishing`;
  };

  // Word-by-word fade-in effect
  const words = currentQuestionConfig?.question.split(" ") || [];
  const [displayedCount, setDisplayedCount] = useState(0);
  const isRevisited = isQuestionRevisited(currentQuestionId);

  useEffect(() => {
    setDisplayedCount(0);
    if (!isWelcomeScreenDismissed || !currentQuestionConfig) return;

    // Skip animation for revisited questions
    if (isRevisited) {
      setDisplayedCount(words.length);

      return;
    }

    const delay = 40;
    const timeouts = words.map((_, idx) =>
      setTimeout(() => {
        setDisplayedCount((c) => c + 1);
      }, idx * delay),
    );

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [
    currentQuestionConfig,
    isWelcomeScreenDismissed,
    isRevisited,
    words.length,
  ]);

  if (showRecommendations) {
    return <RecommendationsDisplay />;
  }

  if (!currentQuestionConfig || !journeyData) {
    return null;
  }

  return (
    <div
      className={cn(
        "w-full justify-center items-center p-8 h-auto 2xl:h-[calc(100vh-64px)] lg:ml-[33%] relative",
      )}
    >
      {/* Background pattern - similar to welcome screen */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(29,78,216,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(147,51,234,0.08),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(59,130,246,0.08),transparent_50%)]" />
      </div>

      <div className="relative z-10">
        <WizardNavigation />
        <div className="overflow-auto items-center justify-center flex flex-col pb-5">
          <div className={cn("w-2/3 mx-auto flex flex-col space-y-2 my-5")}>
            <h2
              className={cn(
                "text-3xl",
                "font-semibold",
                "text-white",
                "min-h-[100px]",
                "flex",
                "flex-wrap",
                "items-center",
                "gap-2",
                "drop-shadow-lg",
              )}
            >
              <span>
                {words.map((word, i) => (
                  <span
                    key={i}
                    className={cn(
                      "inline-block",
                      "transition-all",
                      "duration-300",
                      "ease-out",
                      i < displayedCount
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-2",
                    )}
                  >
                    {word}&nbsp;
                  </span>
                ))}
              </span>
              {displayedCount >= words.length &&
                !showRecommendations &&
                remainingSeconds > 0 && (
                  <Chip
                    className="bg-blue-500/20 text-blue-300 border border-blue-400/30 text-sm px-3 py-1 ml-2 backdrop-blur-sm shadow-lg"
                    size="sm"
                    variant="light"
                  >
                    {formatTimeForChip(remainingSeconds)}
                  </Chip>
                )}
            </h2>
            {currentQuestionConfig.description && (
              <div className={cn("w-full")}>
                <p className={cn("text-lg", "text-gray-300", "drop-shadow-sm")}>
                  {currentQuestionConfig.description}
                </p>
              </div>
            )}
          </div>
          <Options
            currentAnswers={answers[currentQuestionConfig.id]}
            isMultiSelect={!!currentQuestionConfig.multiSelect}
            isRevisited={isRevisited}
            isSkippable={!!currentQuestionConfig.skippable}
            options={currentQuestionConfig.options}
            questionId={currentQuestionConfig.id}
            onSelect={handleSelectAnswer}
          />
        </div>
      </div>
    </div>
  );
}
